import type { Guide, GuideStep } from './types';

/**
 * Reguły przewodnika po zmianach (decyzja użytkownika 21.10.2026) — czyste
 * funkcje bez Reacta: dopasowanie kroku do bieżącego ekranu, dokąd przejść
 * na kolejnym kroku, które przewodniki zaproponować na tym ekranie.
 *
 * Ścieżki to te z części adresu po `#` (react-admin prowadzi trasy
 * w `HashRouter`), bez parametrów zapytania: `/pre-orders/12/show`.
 */

/** Klucz przełącznika samej nakładki (S-21.10-28). Dane — nie przemianowujemy. */
export const GUIDE_FEATURE = 'guideOverlay';

/** Ścieżka bez zapytania i bez końcowego `/` (poza pulpitem `/`). */
export function normalizePath(path: string): string {
  const noQuery = path.split('?')[0].split('#').join('');
  const withSlash = noQuery.startsWith('/') ? noQuery : `/${noQuery}`;
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : withSlash;
}

const segments = (path: string) => normalizePath(path).split('/').filter(Boolean);

/** Nazwy parametrów wzorca (`:preOrderId` → `preOrderId`). */
export function routeParams(pattern: string): string[] {
  return segments(pattern)
    .filter((s) => s.startsWith(':'))
    .map((s) => s.slice(1));
}

/**
 * Parametry ścieżki, gdy pasuje do wzorca, inaczej `null`. Parametr pasuje do
 * jednego dowolnego członu adresu.
 */
export function matchRoute(pattern: string, path: string): Record<string, string> | null {
  const p = segments(pattern);
  const a = segments(path);
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i += 1) {
    if (p[i].startsWith(':')) params[p[i].slice(1)] = decodeURIComponent(a[i]);
    else if (p[i] !== a[i]) return null;
  }
  return params;
}

/** Ścieżka z wypełnionymi parametrami albo `null`, gdy któregoś brakuje. */
export function fillRoute(pattern: string, params: Readonly<Record<string, string>>): string | null {
  const out: string[] = [];
  for (const s of segments(pattern)) {
    if (s.startsWith(':')) {
      const v = params[s.slice(1)];
      if (v === undefined || v === '') return null;
      out.push(encodeURIComponent(v));
    } else out.push(s);
  }
  return `/${out.join('/')}`;
}

/** Pierwszy krok przewodnika pasujący do ekranu; -1 = żaden. */
export function stepIndexForPath(guide: Guide, path: string): number {
  return guide.steps.findIndex((s) => matchRoute(s.route, path) !== null);
}

/**
 * Krok dla bieżącego widoku: pierwszy krok tego ekranu, którego element jest
 * na ekranie (`visible`), a gdy żadnego nie widać — pierwszy krok ekranu.
 * Ten sam ekran bywa różny dla ról (pulpit przyjęcia zleceń: CS widzi swoją
 * część, Administracja swoją), więc krok wybieramy po tym, co widać.
 */
export function stepIndexForScreen(guide: Guide, path: string, visible: (target: string) => boolean): number {
  const matching = guide.steps.map((s, i) => (matchRoute(s.route, path) !== null ? i : -1)).filter((i) => i >= 0);
  if (matching.length === 0) return -1;
  return matching.find((i) => visible(guide.steps[i].target)) ?? matching[0];
}

/** Czy krok dotyczy tego ekranu. */
export const stepMatches = (step: GuideStep, path: string) => matchRoute(step.route, path) !== null;

/**
 * Parametry, które da się wziąć z bieżącego adresu dla tego przewodnika —
 * z każdego kroku, którego wzorzec pasuje. Kroki tego samego prezlecenia
 * (karta → okno kontroli) dzielą nazwę parametru, więc „Dalej” przechodzi na
 * okno kontroli tego samego prezlecenia.
 */
export function knownParams(guide: Guide, path: string): Record<string, string> {
  const params: Record<string, string> = {};
  for (const s of guide.steps) {
    const m = matchRoute(s.route, path);
    if (m) Object.assign(params, m);
  }
  return params;
}

/** Adres listy z filtrem — tak samo jak kafelki pulpitu (`buildListUrl`). */
export function listUrl(listPath: string, filter?: Readonly<Record<string, unknown>>): string {
  const base = normalizePath(listPath);
  if (!filter || Object.keys(filter).length === 0) return base;
  return `${base}?filter=${encodeURIComponent(JSON.stringify(filter))}`;
}

/**
 * Dokąd przejść, żeby zobaczyć krok:
 * - `here` — bieżący ekran jest ekranem kroku;
 * - `route` — konkretny adres (ekran bez rekordu albo rekord znany z adresu);
 * - `record` — trzeba wybrać rekord: lista z filtrem i opis, jaki rekord.
 */
export type StepDestination =
  | { kind: 'here' }
  | { kind: 'route'; path: string }
  | { kind: 'record'; listPath: string; description: string };

export function stepDestination(guide: Guide, index: number, currentPath: string): StepDestination {
  const step = guide.steps[index];
  if (!step) return { kind: 'here' };
  if (stepMatches(step, currentPath)) return { kind: 'here' };
  const filled = fillRoute(step.route, knownParams(guide, currentPath));
  if (filled) return { kind: 'route', path: listUrl(filled, step.routeFilter) };
  if (step.record) {
    return {
      kind: 'record',
      listPath: listUrl(step.record.listPath, step.record.listFilter),
      description: step.record.description,
    };
  }
  // Ścieżka z parametrem bez opisu rekordu — test rejestru tego nie przepuszcza.
  return { kind: 'here' };
}

/**
 * Selektor elementu kroku. `*` na końcu — pierwszy element o identyfikatorze
 * zaczynającym się od tekstu przed gwiazdką.
 */
export function targetSelector(target: string): string {
  const esc = (v: string) => v.replace(/["\\]/g, '\\$&');
  return target.endsWith('*')
    ? `[data-testid^="${esc(target.slice(0, -1))}"]`
    : `[data-testid="${esc(target)}"]`;
}

/** Przewodniki funkcji włączonych dla zalogowanego, które mają krok na tym ekranie. */
export function guidesForPath(
  guides: readonly Guide[],
  path: string,
  isEnabled: (flagKey: string) => boolean,
): Guide[] {
  return guides.filter((g) => isEnabled(g.flagKey) && stepIndexForPath(g, path) >= 0);
}

/**
 * Propozycja „Nowe: <funkcja> — pokaż, jak działa” na tym ekranie: pierwszy
 * nieobejrzany przewodnik włączonej funkcji, którego użytkownik w tej sesji
 * nie odłożył („Nie teraz”). `others` — ile jeszcze nowych ma ten ekran.
 * Sam przewodnik o przewodniku nie jest proponowany na każdym ekranie —
 * tylko tam, gdzie ma krok (jak każdy inny).
 */
export function proposalFor(
  guides: readonly Guide[],
  path: string,
  isEnabled: (flagKey: string) => boolean,
  seen: ReadonlySet<string>,
  dismissed: ReadonlySet<string>,
): { guide: Guide; others: number } | null {
  const fresh = guidesForPath(guides, path, isEnabled).filter(
    (g) => !seen.has(g.flagKey) && !dismissed.has(g.flagKey),
  );
  if (fresh.length === 0) return null;
  return { guide: fresh[0], others: fresh.length - 1 };
}

/** Przewodnik po kluczu przełącznika. */
export function guideFor(guides: readonly Guide[], flagKey: string | null | undefined): Guide | null {
  if (!flagKey) return null;
  return guides.find((g) => g.flagKey === flagKey) ?? null;
}
